import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const TransformCard = ({ image, title, description, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="group"
    >
      <div className="relative rounded-3xl overflow-hidden luxury-card h-full">
        {/* Image */}
        <div className="relative h-64 md:h-72 overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
          
          {/* Gold accent line */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent to-transparent"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.3 + index * 0.15, duration: 0.8 }}
          />
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          <h3 className="font-display text-xl md:text-2xl text-foreground mb-4 leading-tight">
            {title}
          </h3>
          <p className="text-muted-foreground font-body leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export const TransformationSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const transformations = [
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/02/gilles1385_figurative_futurist_photo_human_body_with_chakras_li_f3b9c26d-4156-4461-aafe-d50d95afe9b1.png",
      title: "De ressentir que tu es enfin arrivé·e \"à la maison\" (la source)",
      description: "Peut-être as-tu déjà ressenti cette douce sensation au cours d'un soin énergétique. Mais c'est éphémère ! Je vais t'apprendre à vivre dans cet état de haute vibration au quotidien. Il va devenir ton mode par défaut !"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/02/gilles1385_figurative_futurist_photo_of_woman_exprimant_la_paix_2193e425-c0b7-4671-913e-62e57f8d4064.png",
      title: "De retirer couches après couches tout ce qui t'empêche d'être pleinement heureux·se",
      description: "Libéré·e des deuils, déceptions sentimentales et traumatismes, tu te sentiras plus léger·e et confiant·e pour atteindre tes objectifs dans chaque domaine de ta vie."
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2025/05/gilles1385_Ultra-realistic_photo_of_a_powerful_woman_standing_i_f5941fa6-62e2-491f-b2bc-e1b9e6742aa6.png",
      title: "De faire la paix avec le passé",
      description: "Et toutes les émotions lourdes qui y sont associées. D'arrêter de t'inquiéter d'un futur incertain. D'être tout simplement présent·e à ce qui est."
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_renaissance_new_age_galactic_divine_white_gold_multi_7abf6662-243e-4ba3-95d7-b78d395cf370.png",
      title: "De vivre dans un état de conscience supérieure",
      description: "Fini de te sentir submergé·e par des vagues émotionnelles. Tu vas apprendre à surfer la vie avec autant de fluidité qu'un dauphin. Lâcher prise assuré !"
    },
    {
      image: "https://gillesdelieuze.com/wp-content/uploads/2023/07/gilles_divinite_divine_gold_ascension_avatar_ai_mythos_mystic_Q_90fc83b3-63ab-499d-9605-015536818f16.png",
      title: "De développer tes capacités médiumniques",
      description: "Clairvoyance, intuition, vision du futur ne sont pas réservées à une élite qui aurait \"un don\". Ce sont des capacités à activer. L'énergie des Maîtres Dauphins t'ouvrira ce portail énergétique."
    }
  ];

  return (
    <section ref={sectionRef} className="section-padding bg-background relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container-luxury relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <span className="text-accent font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Fréquences vibratoires
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight mb-6">
            Dans l'école des Maîtres Dauphins, je te partage de 
            <span className="text-gradient-gold"> puissantes fréquences vibratoires</span>
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            que j'ai reçues par canalisation
          </p>
        </motion.div>

        {/* Subtitle */}
        <motion.h3
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.3 }}
          className="text-center text-2xl font-display text-foreground mb-12"
        >
          C'est en t'y connectant qu'elles vont te permettre :
        </motion.h3>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {transformations.slice(0, 3).map((item, index) => (
            <TransformCard key={index} {...item} index={index} />
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-6 md:mt-8 max-w-4xl mx-auto">
          {transformations.slice(3).map((item, index) => (
            <TransformCard key={index + 3} {...item} index={index + 3} />
          ))}
        </div>

        {/* Dolphin Image Accent */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: 0.8, duration: 1 }}
          className="absolute right-0 top-1/2 -translate-y-1/2 hidden xl:block pointer-events-none"
        >
          <img
            src="https://gillesdelieuze.com/wp-content/uploads/2025/02/gilles1385_an_anime_style_and_luminous_dolphin_is_glowing_green_f8912b72-a338-40aa-a4cb-75effd9294b7-574x1024.png"
            alt="Dauphin lumineux"
            className="w-48 opacity-30"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default TransformationSection;
